import React, { useEffect, useState } from 'react';
import CustomTh from '../../../../../components/table/CustomTh';
import CustomTd from '../../../../../components/table/CustomTd';
import TableFunction from '../../../../../components/table/TableFunction';
import { useTranslation } from 'react-i18next';
import { useDispatch } from "react-redux";
import { GetAllCardFormats } from "../../../../../reduxToolkit/Cards/CardsApi";
import { useParams } from "react-router-dom";
import { GetHidRecordDetails } from "../../../../../reduxToolkit/HID/HidApi";
import { TABLES } from "../../../../../Apis/Tables";
import { toast } from "react-toastify";
import {DeleteItemsApi} from "../../../../../reduxToolkit/Commons/CommonsApi";
import {EMPTY_VALUES, errorMsg} from "../../../../../Apis/functions";

/**
 * @author Karen Yunuen Pozos Arias
 * @position Full Stack Developer - IBL
 * @contact kpozos@ibl.mx
 *
 * @description
 * FormatTableApp handles displaying and managing the card formats table for a specific controller or SIO.
 * It supports selection, deletion, adding new formats, and refresh after modification.
 *
 * @param {Array} defaultFormatList - Optional default list of formats to initialize the table.
 *
 * @returns {Object} - Table handlers, selection state, modal controls, and CRUD functions.
 */
const FormatTableApp = ({ defaultFormatList }) => {
    const { t } = useTranslation();
    const { id } = useParams();
    const dispatch = useDispatch();

    const [tableId, setTableId] = useState("tbl_ftc_table");
    const [deleteFormatShow, setDeleteFormatShow] = useState(false);
    const [selectedList, setSelectedList] = useState([]);
    const [isAllChecked, setIsAllChecked] = useState(false);
    const [showCreateFormatModal, setShowCreateFormatModal] = useState(false);
    const [modified, setModified] = useState(false);
    const [results, setResults] = useState({ content: [], totalElements: 0 });

    /**
     * Initializes table data if defaultFormatList is provided.
     */
    useEffect(() => {
        if (defaultFormatList?.length > 0)
            setResults({ content: defaultFormatList || [], totalElements: defaultFormatList?.length });
    }, [defaultFormatList]);

    /**
     * Builds the table header (thead) for the formats table.
     *
     * @returns {Array<JSX.Element>} Array of <CustomTh> elements for column headers.
     */
    const getThead = () => [
        <CustomTh tableId={tableId} className='first_head'>{t('name')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('facility_code')}</CustomTh>
    ];

    /**
     * Builds the table body (tbody) displaying formats data.
     *
     * @returns {Object} Object containing JSX body and raw data.
     */
    const getTbody = () => {
        if (!results || results?.content?.length === 0) return { body: [], data: [] };

        const body = results?.content.map((item) => (
            <>
                <CustomTd tableId={tableId} className='first-column'>{item?.formatName || EMPTY_VALUES.DEFAULT}</CustomTd>
                <CustomTd tableId={tableId}>{item?.facilityCode || EMPTY_VALUES.DEFAULT}</CustomTd>
            </>
        ));

        return { body, data: results?.content };
    };

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page) => {
        dispatch(GetHidRecordDetails({ id })).then(({ payload: { data: { data, success } } }) => {
            if (success) {
                setResults({ content: data.formats, totalElements: data.formats.length });
            }
        });
    }

    /**
     * Table utility hook to handle selection, rendering, and delete operations.
     */
    const { thead, tbody, handleCheckboxChange, handleDeleteAll } = TableFunction({
        selectedList,
        setSelectedList,
        data: results,
        getTbody,
        getThead,
        setIsAllChecked,

        getData,//New
        modified,//New
        setModified,//New
    });

    /**
     * Opens the modal to add a new card format and fetches all available card formats.
     */
    const handleAddButtonClick = () => {
        dispatch(GetAllCardFormats());
        setShowCreateFormatModal(true);
    };

    /**
     * Deletes selected formats from the backend and refreshes the table.
     */
    const deleteSelectedFormat = () => {
        const body = selectedList;
        if (body.length > 0) {
            dispatch(DeleteItemsApi({ tableName: TABLES.HID_CARD_FORMAT, body })).then(({ payload: { data: { success, code } } }) => {
                if (success === true) {
                    setSelectedList([]);
                    setIsAllChecked(false);
                    setModified(true)
                }else errorMsg(code);
            });
        }
    };

    return {
        t,
        id,
        thead,
        tbody,
        tableId,
        selectedList,
        setDeleteFormatShow,
        showCreateFormatModal,
        setShowCreateFormatModal,
        deleteFormatShow,
        isAllChecked,
        setIsAllChecked,
        setSelectedList,
        setModified,
        deleteSelectedFormat,
        handleCheckboxChange,
        handleDeleteAll,
        handleAddButtonClick,
    };
};

export default FormatTableApp;
